import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AxiosError } from 'axios';
import { createCards, CreateCardError, parseCreateError, useWorkspaceStore } from '@features/CardsWorkspace';
import { fetchTopicAvailability, topicKeys, type TopicBatch } from '@features/CardsWorkspace/api/topicCardsApi';
import { startToastBatch } from '@features/Notifications';
import { generationKeys } from '@entities/Generation';
import { moduleKeys, rememberComposerModule } from '@entities/Module';
import { fetchQuota, planKeys, useQuotaQuery } from '@entities/Plan';
import { wordKeys } from '@entities/Word';
import { buildUnitPath, paths } from '@shared/config';
import type { ModuleSummary } from '@shared/types';
import { cn, newUuid } from '@shared/lib';
import {
  createTopicSelection,
  pairAvailability,
  selectionCost,
  topicGroups,
  type BulkProgress,
  type BulkResult,
  type PairAvailability,
  type TopicPair,
} from './selectionModel';
import { HoldPeek, TopicChip, type ChipTone, type PeekState } from './TopicChip';

/** Код ошибки пачки, проверки доступности или квоты. 429 различаем по `error.code`: жетоны
 *  (`DAILY_GENERATION_LIMIT_REACHED`) или ограничитель частоты, а не любой 429 — лимит жетонов. */
function failureCode(error: unknown): string {
  if (error instanceof CreateCardError) return error.code;
  if (error instanceof AxiosError) {
    return parseCreateError(error.response ? error.response.status : null, error.response?.data).code;
  }
  return 'UNKNOWN';
}

const ERROR_KEYS: Record<string, string> = {
  DAILY_LIMIT_REACHED: 'topicCards.cardQuota',
  NOT_IN_DICTIONARY: 'topicCards.pairUnavailable',
  PICK_LIMIT: 'topicCards.pickLimit',
  GENERATION_IN_PROGRESS: 'cardsWorkspace.errors.generationInProgress',
  ALREADY_ADDED: 'cardsWorkspace.errors.alreadyAdded',
  RATE_LIMIT: 'cardsWorkspace.errors.rateLimit',
  MODULE_NOT_FOUND: 'cardsWorkspace.errors.moduleNotFound',
  EMAIL_NOT_VERIFIED: 'cardsWorkspace.errors.emailNotVerified',
  SERVER: 'cardsWorkspace.errors.serverUncertain',
  GENERATION_PART_IN_PROGRESS: 'topicCards.uncertainResult',
  NETWORK: 'topicCards.uncertainResult',
  UNKNOWN: 'topicCards.uncertainResult',
};

export function TopicSelection({
  batch,
  userId,
  module,
  onBusyChange,
}: {
  batch: TopicBatch;
  userId: string;
  module: ModuleSummary | null;
  onBusyChange: (busy: boolean) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const groups = useMemo(() => topicGroups(batch.items), [batch.items]);
  // По умолчанию ничего не отмечено (решение владельца 2026-10-16): с отмеченными всеми яркая
  // кнопка «Создать» сразу тратила жетоны на все переводы — её нажимали случайно.
  const [excluded, setExcluded] = useState<Set<string>>(
    () => new Set(groups.flatMap((group) => group.pairs.map((pair) => pair.id))),
  );
  const [accepted, setAccepted] = useState<Set<string>>(() => new Set());
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<BulkProgress | null>(null);
  const [result, setResult] = useState<BulkResult | null>(null);
  const [fatalError, setFatalError] = useState<string | null>(null);
  const [peek, setPeek] = useState<PeekState>(null);
  const runningRef = useRef(false);
  const activeRef = useRef(true);
  const readAbort = useRef<AbortController | null>(null);
  const quota = useQuotaQuery();
  const refreshTick = useWorkspaceStore((state) => state.refreshTick);
  const lastRefresh = useRef(refreshTick);
  const queryKey = topicKeys.availability(userId, batch.id, module?.id ?? null);
  const availability = useQuery({
    queryKey,
    queryFn: ({ signal }) => fetchTopicAvailability(batch.id, module!.id, signal),
    enabled: !!module && !busy,
    staleTime: 0,
    retry: 1,
    refetchInterval: (query) =>
      !busy && query.state.data?.items.some((item) => item.senses.some((sense) => sense.generating)) ? 5000 : false,
  });
  useEffect(() => {
    activeRef.current = true;
    return () => {
      activeRef.current = false;
      readAbort.current?.abort();
      onBusyChange(false);
    };
  }, [onBusyChange]);
  useEffect(() => {
    if (lastRefresh.current === refreshTick) return;
    lastRefresh.current = refreshTick;
    if (!busy && module) void qc.invalidateQueries({ queryKey: topicKeys.availability(userId, batch.id, module.id) });
  }, [refreshTick, busy, module?.id, userId, batch.id, qc]);

  const states: Record<string, PairAvailability> = {};
  groups.forEach((group) =>
    group.pairs.forEach((pair) => {
      if (availability.data) states[pair.id] = pairAvailability(pair, availability.data);
    }),
  );
  const eligible = groups
    .flatMap((group) => group.pairs)
    .filter((pair) => !accepted.has(pair.id) && states[pair.id]?.state === 'available');
  const selectedGroups = groups
    .map((group) => ({
      ...group,
      pairs: group.pairs.filter(
        (pair) => !excluded.has(pair.id) && !accepted.has(pair.id) && states[pair.id]?.state === 'available',
      ),
    }))
    .filter((group) => group.pairs.length);
  const selectedCount = selectedGroups.reduce((total, group) => total + group.pairs.length, 0);
  // Пока модуль не выбран, доступность неизвестна: показываем, сколько значений отмечено.
  const markedCount = availability.data
    ? selectedCount
    : groups.reduce((total, group) => total + group.pairs.filter((pair) => !excluded.has(pair.id)).length, 0);
  const meaningCount = groups.reduce((total, group) => total + group.pairs.length, 0);
  const overPickLimit = selectedGroups.some((group) => group.pairs.length > 10);
  const required = overPickLimit ? 0 : selectionCost(selectedGroups, states);
  const spendable = quota.data ? quota.data.remaining + quota.data.bonus : 0;
  const insufficient = !!quota.data && spendable < required;
  const canCreate =
    !!module &&
    !!availability.data &&
    !availability.isFetching &&
    !availability.isError &&
    !!quota.data &&
    !quota.isError &&
    !insufficient &&
    !overPickLimit &&
    selectedCount > 0 &&
    !busy;
  const allSelected = eligible.length > 0 && eligible.every((pair) => !excluded.has(pair.id));
  // На кнопке — что именно будет и сколько стоит: «Создать 12 карточек · 2 жетона» (2026-10-16).
  const costKnown = !!availability.data && !availability.isFetching && !overPickLimit;
  const createLabel = busy
    ? t('topicCards.bulkWorking')
    : !module
      ? t('topicCards.chooseModuleShort')
      : selectedCount > 0 && costKnown
        ? t('topicCards.createCards', {
            // Карточка — слово или фраза, а не перевод: три перевода одного слова — одна карточка (ревью 2026-10-19).
            count: selectedGroups.length,
            cost: required > 0 ? t('topicCards.generations', { count: required }) : t('topicCards.free'),
          })
        : eligible.length > 0 && selectedCount === 0 && availability.data
          ? t('topicCards.pickAbove')
          : t('topicCards.createIn', { module: module.title });
  // Первая проверка доступности в этом модуле: чипы — скелетон с бликом, а не «серый выбор».
  const checking = !!module && availability.isPending;

  const run = async () => {
    if (!canCreate || !module || runningRef.current) return;
    runningRef.current = true;
    const targetModule = module;
    const selected = selectedGroups;
    const controller = new AbortController();
    readAbort.current = controller;
    setBusy(true);
    onBusyChange(true);
    setResult(null);
    setFatalError(null);
    const queuedCards: { cardId: string; front: string }[] = [];
    // Одно нажатие — одна запись «Генераций», сколько бы пачек ни ушло (ТЗ главной § 22.12).
    const generationId = newUuid();
    try {
      await qc.cancelQueries({ queryKey });
      const outcome = await createTopicSelection(selected, {
        isActive: () => activeRef.current && !controller.signal.aborted,
        availability: async () => {
          const fresh = await fetchTopicAvailability(batch.id, targetModule.id, controller.signal);
          qc.setQueryData(queryKey, fresh);
          return fresh;
        },
        quota: async () => {
          const fresh = await fetchQuota();
          qc.setQueryData(planKeys.quota, fresh);
          return fresh;
        },
        create: async (pack, press) => {
          const picks = (pairs: TopicPair[]) => pairs.map((pair) => ({ pos: pair.pos, translation: pair.translation }));
          const [first, ...rest] = pack;
          const created = await createCards({
            front: first.word,
            moduleId: targetModule.id,
            moduleTitle: targetModule.title,
            difficulty: 'B1',
            topicBatchId: batch.id,
            picks: picks(first.pairs),
            words: rest.map((group) => ({ front: group.word, picks: picks(group.pairs) })),
            generationId: press.generationId,
            generationPart: press.part,
          });
          queuedCards.push(
            ...created.cards
              .filter((card) => card.status === 'queued' || card.status === 'generating')
              .map((card) => ({ cardId: card.cardId, front: card.payload.front })),
          );
          return { skipped: created.skipped };
        },
        errorCode: failureCode,
        onProgress: (next) => {
          if (activeRef.current) setProgress(next);
        },
        onAccepted: (ids) => {
          if (activeRef.current && ids.length) setAccepted((current) => new Set([...current, ...ids]));
        },
      }, generationId);
      // Модуль по умолчанию для строки создания на главной (ТЗ § 9).
      if (outcome.added.length) rememberComposerModule(userId, targetModule);
      if (activeRef.current) setResult(outcome);
    } catch (error) {
      if (activeRef.current) setFatalError(failureCode(error));
    } finally {
      runningRef.current = false;
      readAbort.current = null;
      startToastBatch(queuedCards);
      useWorkspaceStore.getState().bumpRefresh();
      void qc.invalidateQueries({ queryKey: moduleKeys.listsRoot });
      void qc.invalidateQueries({ queryKey: moduleKeys.byId(targetModule.id) });
      void qc.invalidateQueries({ queryKey: wordKeys.byUnit(targetModule.id) });
      void qc.invalidateQueries({ queryKey: planKeys.quota });
      void qc.invalidateQueries({ queryKey: planKeys.billing });
      void qc.invalidateQueries({ queryKey });
      void qc.invalidateQueries({ queryKey: generationKeys.list() });
      if (activeRef.current) {
        setBusy(false);
        onBusyChange(false);
      }
    }
  };
  const errorText = (code: string) => t(ERROR_KEYS[code] ?? 'cardsWorkspace.errors.unknownCreate');
  const holdPair =
    peek?.mode === 'hold' ? groups.flatMap((group) => group.pairs).find((pair) => pair.id === peek.id) : undefined;
  const toggle = (id: string) =>
    setExcluded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="tc-selection" aria-busy={busy || undefined}>
      <div className="tc-list-head">
        <span>
          {t('topicCards.wordCount', { count: groups.length })} · {t('topicCards.meanings', { count: meaningCount })}
        </span>
        {checking ? (
          <span className="tc-checking" role="status">
            {t('topicCards.checking')}
          </span>
        ) : (
          <button
            type="button"
            className="tc-link"
            disabled={busy || !eligible.length}
            onClick={() =>
              setExcluded(
                allSelected ? new Set(groups.flatMap((group) => group.pairs.map((pair) => pair.id))) : new Set(),
              )
            }
          >
            {t(allSelected ? 'topicCards.deselectAll' : 'topicCards.selectAll')}
          </button>
        )}
      </div>
      {!module && <p className="tc-note tc-note--accent">{t('topicCards.chooseModule')}</p>}
      {/* Слова подбора сами в модуль не попадают — тестировщик думал, что уже добавлены (2026-10-16). */}
      {module && eligible.length > 0 && <p className="tc-note">{t('topicCards.notAddedYet')}</p>}
      {module && availability.isError && (
        <p className="tc-note tc-error" role="alert">
          {t('topicCards.availabilityError')}{' '}
          <button type="button" className="tc-link" disabled={busy} onClick={() => void availability.refetch()}>
            {t('topicCards.retry')}
          </button>
        </p>
      )}
      <ul className={cn('tc-words', checking && 'is-checking')} aria-busy={checking || undefined}>
        {groups.map((group) => {
          const showPos = new Set(group.pairs.map((pair) => pair.pos ?? 'other')).size > 1;
          const errors = [
            ...new Set(group.pairs.map((pair) => result?.failed[pair.id]).filter((code): code is string => !!code)),
          ];
          return (
            <li key={group.word} className="tc-word">
              <div className="tc-word-head">
                <div className="tc-word-line">
                  <span className="tc-word-name">{group.word}</span>
                  {group.ipa && <span className="tc-word-ipa">{group.ipa}</span>}
                </div>
                {group.kind && (
                  <span className="tc-word-kind">
                    {t(group.kind === 'phrasal' ? 'topicCards.kindPhrasal' : 'topicCards.kindPhrase')}
                  </span>
                )}
              </div>
              <div className="tc-chips">
                {group.pairs.map((pair) => {
                  const pairState = states[pair.id];
                  const added = accepted.has(pair.id);
                  const selectable = pairState?.state === 'available' && !added;
                  const failed = result?.failed[pair.id];
                  const tone: ChipTone = added || pairState?.state === 'learned'
                    ? 'done'
                    : pairState?.state === 'generating'
                      ? 'generating'
                      : pairState?.state === 'unavailable'
                        ? 'unavailable'
                        : pairState === undefined
                          ? excluded.has(pair.id) ? 'off' : 'pending'
                          : excluded.has(pair.id) ? 'off' : 'on';
                  const tag = added
                    ? t('topicCards.added')
                    : pairState?.state === 'learned'
                      ? t('topicCards.pairLearned')
                      : pairState?.state === 'generating'
                        ? t('topicCards.pairGenerating')
                        : pairState?.state === 'unavailable'
                          ? t('topicCards.tagUnavailable')
                          : selectable && pairState.cost === 0
                            ? t('topicCards.pairFree')
                            : null;
                  return (
                    <TopicChip
                      key={pair.id}
                      pair={pair}
                      tone={tone}
                      locked={busy || !module || !selectable || availability.isError}
                      showPos={showPos}
                      tag={tag}
                      note={
                        pairState?.state === 'unavailable'
                          ? t('topicCards.pairUnavailable')
                          : failed
                            ? errorText(failed)
                            : undefined
                      }
                      failed={!!failed}
                      peek={peek}
                      onPeek={setPeek}
                      onToggle={() => toggle(pair.id)}
                    />
                  );
                })}
              </div>
              {errors.length > 0 && <p className="tc-word-error">{errors.map(errorText).join(' ')}</p>}
            </li>
          );
        })}
      </ul>
      {holdPair && <HoldPeek pair={holdPair} onClose={() => setPeek(null)} />}

      <div className="tc-footer">
        <div className="tc-footer-summary">
          <strong>
            {markedCount > 0 ? t('topicCards.meanings', { count: markedCount }) : t('topicCards.nothingSelected')}
          </strong>
          {checking && markedCount > 0 && (
            <span className="tc-footer-cost is-checking">{t('topicCards.checkingCost')}</span>
          )}
          {selectedCount > 0 && availability.data && (
            <span className="tc-footer-cost">
              {required > 0 ? t('topicCards.generations', { count: required }) : t('topicCards.free')}
            </span>
          )}
          <p>{t('topicCards.packHint')}</p>
        </div>
        <button type="button" className="tc-primary" disabled={!canCreate} onClick={() => void run()}>
          {createLabel}
        </button>
        {busy && progress && (
          <div className="tc-footer-row" role="status">
            <progress max={progress.total || 1} value={progress.phase === 'checking' ? 0 : progress.processed} />
            <span>
              {progress.phase === 'checking'
                ? t('topicCards.bulkChecking')
                : t('topicCards.bulkProgress', { done: progress.processed, total: progress.total })}{' '}
              · {t('topicCards.bulkStay')}
            </span>
          </div>
        )}
        {insufficient && !busy && (
          <p className="tc-footer-row tc-error" role="status">
            {t('topicCards.bulkQuota', { required, available: spendable })}{' '}
            <Link to={paths.plans}>{t('topicCards.plans')}</Link>
          </p>
        )}
        {overPickLimit && <p className="tc-footer-row tc-error">{t('topicCards.pickLimit')}</p>}
        {quota.isError && (
          <p className="tc-footer-row tc-error">
            {t('topicCards.quotaError')}{' '}
            <button type="button" className="tc-link" onClick={() => void quota.refetch()}>
              {t('topicCards.retry')}
            </button>
          </p>
        )}
        {result && !busy && (
          <div className="tc-footer-row tc-footer-result" role="status">
            <span>{t('topicCards.bulkSummary', { added: result.added.length, already: result.already.length })}</span>
            {result.quota && (
              <span className="tc-error">
                {t('topicCards.bulkQuota', { required: result.quota.required, available: result.quota.available })}
              </span>
            )}
            {module && result.added.length > 0 && (
              <Link className="tc-link" to={buildUnitPath(module.id)}>
                {t('topicCards.toCards', { module: module.title })} →
              </Link>
            )}
          </div>
        )}
        {fatalError && (
          <p className="tc-footer-row tc-error" role="alert">
            {errorText(fatalError)}
          </p>
        )}
      </div>
    </div>
  );
}
